import React, { useEffect, useState, useMemo } from 'react';
import {
  FiExternalLink,
  FiUser,
  FiMail,
  FiMapPin,
  FiFileText,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiUsers,
  FiHome,
  FiArrowUp,
  FiArrowDown,
} from 'react-icons/fi';
import { Spinner } from 'react-bootstrap';
import { format, formatarMonetario } from '~/Utils/Helpers';
import Header from '~/Components/Header/Header';
import * as api from '~/Services/Service';
import { gerarAmbienteUrl } from '~/Pages/Vendas/helpers';
import './CardSituacaoDetalhada.css';

const StatusBadge = ({ status, size = 'md' }) => {
  const getStatusConfig = status => {
    const normalizedStatus = status.toLowerCase() || '';
    if (normalizedStatus.includes('inadimpl')) {
      return {
        icon: FiAlertCircle,
        bgColor: '#fef2f2',
        textColor: '#b91c1c',
        borderColor: '#ef4444',
      };
    }
    if (normalizedStatus.includes('adimpl')) {
      return {
        icon: FiCheckCircle,
        bgColor: '#d1fae5',
        textColor: '#047857',
        borderColor: '#10b981',
      };
    }
    if (normalizedStatus.includes('novo')) {
      return {
        icon: FiUser,
        bgColor: '#e0f2fe',
        textColor: '#0369a1',
        borderColor: '#0ea5e9',
      };
    }
    return {
      icon: FiClock,
      bgColor: '#f3f4f6',
      textColor: '#374151',
      borderColor: '#9ca3af',
    };
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding:
          size === 'sm' ? '4px 10px' : size === 'lg' ? '8px 16px' : '6px 12px',
        borderRadius: '50px',
        fontWeight: 600,
        fontSize: size === 'sm' ? '12px' : size === 'lg' ? '14px' : '13px',
        whiteSpace: 'nowrap',
        backgroundColor: config.bgColor,
        color: config.textColor,
        border: `2px solid ${config.borderColor}`,
      }}
    >
      <Icon
        size={size === 'lg' ? 16 : 12}
        style={{ color: config.textColor }}
      />
      <span style={{ color: config.textColor }}>{status}</span>
    </span>
  );
};

const InfoCard = ({ icon: Icon, label, value, link }) => (
  <div className="info-card">
    <div className="info-card__icon">
      <Icon size={18} />
    </div>
    <div className="info-card__content">
      <span className="info-card__label">{label}</span>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noreferrer noopener"
          className="info-card__value info-card__value--link"
        >
          {value} <FiExternalLink size={12} />
        </a>
      ) : (
        <span className="info-card__value">{value || '-'}</span>
      )}
    </div>
  </div>
);

const StatCard = ({ label, value, variant = 'default', icon: Icon }) => (
  <div className={`stat-card stat-card--${variant}`}>
    {Icon && <Icon className="stat-card__icon" size={20} />}
    <span className="stat-card__value">{value}</span>
    <span className="stat-card__label">{label}</span>
  </div>
);

const SortableHeader = ({ label, sortKey, currentSort, onSort }) => {
  const isActive = currentSort.key === sortKey;
  const isAsc = isActive && currentSort.direction === 'asc';
  const isDesc = isActive && currentSort.direction === 'desc';

  const handleClick = () => {
    if (!isActive) {
      onSort({ key: sortKey, direction: 'asc' });
    } else if (isAsc) {
      onSort({ key: sortKey, direction: 'desc' });
    } else {
      onSort({ key: null, direction: null });
    }
  };

  return (
    <th onClick={handleClick} className="sortable-header">
      <div className="sortable-header__content">
        <span>{label}</span>
        <span className="sortable-header__icons">
          <FiArrowUp
            size={12}
            className={`sortable-header__icon ${
              isAsc ? 'sortable-header__icon--active' : ''
            }`}
          />
          <FiArrowDown
            size={12}
            className={`sortable-header__icon ${
              isDesc ? 'sortable-header__icon--active' : ''
            }`}
          />
        </span>
      </div>
    </th>
  );
};

const ContratoRow = ({ contrato, dataVenda }) => {
  const formatarData = dateStr => {
    if (!dateStr || dateStr === '0001-01-01T00:00:00') return '-';
    try {
      return format.date(dateStr);
    } catch {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    }
  };

  const getSituacaoContrato = situacao => {
    const config = {
      Financiado: { className: 'tag--warning', label: 'Financiado' },
      Transferido: { className: 'tag--info', label: 'Transferido' },
      Rescindido: { className: 'tag--neutral', label: 'Rescindido' },
      Quitado: { className: 'tag--success', label: 'Quitado' },
    };
    return config[situacao] || { className: 'tag--neutral', label: situacao };
  };

  const situacaoConfig = getSituacaoContrato(contrato.situacao_contrato);

  return (
    <tr className="contrato-row">
      <td data-label="Quadra/Lote">
        <span className="contrato-row__lote">
          <strong>{contrato.quadra}</strong>/{contrato.lote}
        </span>
      </td>
      <td data-label="Código Boleto">
        <span className="contrato-row__codigo">{contrato.codigo_boleto}</span>
      </td>
      <td data-label="Data Venda">{formatarData(contrato.data_venda)}</td>
      <td data-label="Qtd. Parcelas Pagas">{contrato.qtd_parcelas_pagas}</td>
      <td data-label="Valor Total Pago ">
        {' '}
        {formatarMonetario(contrato.valor_pago)}
      </td>
      <td data-label="Parcelas Vencidas">
        <span
          className={`contrato-row__parcelas ${
            contrato.quantidade_parcelas_vencidas > 0
              ? 'contrato-row__parcelas--danger'
              : ''
          }`}
        >
          {contrato.quantidade_parcelas_vencidas}
        </span>
      </td>
      <td data-label="Valor Vencido">
        <span
          className={`contrato-row__valor ${
            contrato.valor_parcelas_vencidas > 0
              ? 'contrato-row__valor--danger'
              : ''
          }`}
        >
          {formatarMonetario(contrato.valor_parcelas_vencidas) || formatarMonetario('0')}
        </span>
      </td>
      <td data-label="Última Parcela Paga">
        {formatarData(contrato.data_ultima_parcela_paga)}
      </td>
      <td data-label="Parcela Mais Atrasada">
        {formatarData(contrato.data_parcela_mais_atrasada)}
      </td>
      <td data-label="Status Contrato">
        <span className={`tag ${situacaoConfig.className}`}>
          {situacaoConfig.label}
        </span>
      </td>
      <td data-label="Situação">
        {['Rescindido', 'Transferido'].includes(contrato.situacao_contrato) ? (
          '-'
        ) : (
          <StatusBadge status={contrato.situacao_cliente} size="sm" />
        )}
      </td>
    </tr>
  );
};

const ContratosTable = ({ contratos, dataVenda }) => {
  const [sort, setSort] = useState({ key: null, direction: null });

  const sortedContratos = useMemo(() => {
    if (!sort.key) return contratos;

    return [...contratos].sort((a, b) => {
      let valueA, valueB;

      switch (sort.key) {
        case 'quadra_lote':
          valueA = `${a.quadra}${a.lote}`;
          valueB = `${b.quadra}${b.lote}`;
          break;
        case 'codigo_boleto':
          valueA = a.codigo_boleto;
          valueB = b.codigo_boleto;
          break;
        case 'parcelas_vencidas':
          valueA = a.quantidade_parcelas_vencidas || 0;
          valueB = b.quantidade_parcelas_vencidas || 0;
          break;
        case 'valor_vencido':
          valueA = a.valor_parcelas_vencidas || 0;
          valueB = b.valor_parcelas_vencidas || 0;
          break;
        case 'ultima_parcela':
          valueA = a.data_ultima_parcela_paga
            ? new Date(a.data_ultima_parcela_paga).getTime()
            : 0;
          valueB = b.data_ultima_parcela_paga
            ? new Date(b.data_ultima_parcela_paga).getTime()
            : 0;
          break;
        case 'parcela_atrasada':
          valueA = a.data_parcela_mais_atrasada
            ? new Date(a.data_parcela_mais_atrasada).getTime()
            : 0;
          valueB = b.data_parcela_mais_atrasada
            ? new Date(b.data_parcela_mais_atrasada).getTime()
            : 0;
          break;
        case 'status_contrato':
          valueA = a.situacao_contrato || '';
          valueB = b.situacao_contrato || '';
          break;
        case 'situacao':
          valueA = a.situacao_cliente || '';
          valueB = b.situacao_cliente || '';
          break;
        default:
          return 0;
      }

      if (typeof valueA === 'string') {
        const comparison = valueA.localeCompare(valueB, 'pt-BR');
        return sort.direction === 'asc' ? comparison : -comparison;
      }

      if (sort.direction === 'asc') {
        return valueA - valueB;
      }
      return valueB - valueA;
    });
  }, [contratos, sort]);

  return (
    <div className="table-responsive">
      <table className="contratos-table">
        <thead>
          <tr>
            <SortableHeader
              label="Quadra/Lote"
              sortKey="quadra_lote"
              currentSort={sort}
              onSort={setSort}
            />
            <SortableHeader
              label="Cód. Boleto"
              sortKey="codigo_boleto"
              currentSort={sort}
              onSort={setSort}
            />
            <th className="sortable-header">
              <div className="sortable-header__content">
                <span>Data Venda</span>
              </div>
            </th>
            <SortableHeader
              label="Parcelas Pagas"
              sortKey="qtd_parcelas_pagas"
              currentSort={sort}
              onSort={setSort}
            />
            <SortableHeader
              label="Valor Total Pago"
              sortKey="valor_pago"
              currentSort={sort}
              onSort={setSort}
            />
            <SortableHeader
              label="Parc. Vencidas"
              sortKey="parcelas_vencidas"
              currentSort={sort}
              onSort={setSort}
            />
            <SortableHeader
              label="Valor Vencido"
              sortKey="valor_vencido"
              currentSort={sort}
              onSort={setSort}
            />
            <SortableHeader
              label="Última Parc. Paga"
              sortKey="ultima_parcela"
              currentSort={sort}
              onSort={setSort}
            />
            <SortableHeader
              label="Parc. Mais Atrasada"
              sortKey="parcela_atrasada"
              currentSort={sort}
              onSort={setSort}
            />
            <SortableHeader
              label="Status Contrato"
              sortKey="status_contrato"
              currentSort={sort}
              onSort={setSort}
            />
            <SortableHeader
              label="Situação"
              sortKey="situacao"
              currentSort={sort}
              onSort={setSort}
            />
          </tr>
        </thead>
        <tbody>
          {sortedContratos.map((contrato, idx) => (
            <ContratoRow key={idx} contrato={contrato} dataVenda={dataVenda} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EmpreendimentoCard = ({ empreendimento, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const contratosInadimplentes = empreendimento.contratos.filter(c =>
    c.situacao_cliente.toLowerCase().includes('inadimpl')
  ).length;

  const valorTotalVencido = empreendimento.contratos.reduce(
    (acc, c) => acc + (c.valor_parcelas_vencidas || 0),
    0
  );

  return (
    <div
      className={`empreendimento-card ${
        isExpanded ? 'empreendimento-card--expanded' : ''
      }`}
    >
      <div
        className="empreendimento-card__header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="empreendimento-card__info">
          <FiHome className="empreendimento-card__icon" size={24} />
          <div className="empreendimento-card__details">
            <h3 className="empreendimento-card__title">
              {empreendimento.nome}
            </h3>
            <div className="empreendimento-card__meta">
              <span>Código: {empreendimento.codigo}</span>
              <span>Conta: {empreendimento.codigo_conta}</span>
              <span>GE: {empreendimento.ge}</span>
            </div>
          </div>
        </div>

        <div className="empreendimento-card__stats">
          <div className="empreendimento-card__stat">
            <span className="empreendimento-card__stat-value">
              {empreendimento.contratos.length}
            </span>
            <span className="empreendimento-card__stat-label">Contratos</span>
          </div>
          {contratosInadimplentes > 0 && (
            <div className="empreendimento-card__stat empreendimento-card__stat--danger">
              <span className="empreendimento-card__stat-value">
                {contratosInadimplentes}
              </span>
              <span className="empreendimento-card__stat-label">Inadimpl.</span>
            </div>
          )}
          {valorTotalVencido > 0 && (
            <div className="empreendimento-card__stat empreendimento-card__stat--warning">
              <span className="empreendimento-card__stat-value">
                {formatarMonetario(valorTotalVencido)}
              </span>
              <span className="empreendimento-card__stat-label">Vencido</span>
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="empreendimento-card__content">
          <ContratosTable
            contratos={empreendimento.contratos}
            dataVenda={empreendimento.data_venda}
          />
        </div>
      )}
    </div>
  );
};

const AgregadoCard = ({ agregado, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div
      className={`agregado-card ${isExpanded ? 'agregado-card--expanded' : ''}`}
    >
      <div
        className="agregado-card__header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="agregado-card__info">
          <div className="agregado-card__avatar">
            <FiUser size={20} />
          </div>
          <div className="agregado-card__details">
            <h4 className="agregado-card__name">{agregado.nome}</h4>
            <div className="agregado-card__meta">
              <a
                href={`/cliente/pesquisa/situacao/${agregado.cpf}`}
                target="_blank"
                rel="noopener noreferrer"
                className="agregado-card__cpf-link"
              >
                CPF: {format.cpf(agregado.cpf)}
              </a>
              <span className="agregado-card__tipo">
                {agregado.tipo_agregacao}
              </span>
            </div>
          </div>
        </div>

        <div className="agregado-card__stats">
          <div className="agregado-card__stat">
            <span className="agregado-card__stat-value">
              {agregado.quantidade_contratos}
            </span>
            <span className="agregado-card__stat-label">Contratos</span>
          </div>
          <StatusBadge status={agregado.situacao} size="md" />
        </div>
      </div>

      {isExpanded && (
        <div className="agregado-card__content">
          {agregado.empreendimentos.map((emp, idx) => (
            <EmpreendimentoCard key={idx} empreendimento={emp} />
          ))}
        </div>
      )}
    </div>
  );
};

const CardSituacaoDetalhada = props => {
  const [dados, setDados] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const cpf =
    props.cpf || (props.match && props.match.params && props.match.params.cpf);

  const buscarDadosCliente = async () => {
    try {
      setIsLoading(true);
      const response = await api.getPesquisaStatusSituacaoCliente(cpf);
      setDados(response);
      setIsLoading(false);
    } catch (err) {
      console.error('Erro ao buscar dados do cliente', err);
      setError('Erro ao carregar os detalhes da situação do cliente');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    buscarDadosCliente();
  }, [cpf]);

  if (isLoading) {
    return (
      <>
        <div className="situacao-loading">
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="mr-2"
            />
            Carregando Dados...
          </>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="situacao-error">
          <FiAlertCircle size={48} />
          <h3>Ops! Algo deu errado</h3>
          <p>{error}</p>
          <button onClick={buscarDadosCliente} className="btn-retry">
            Tentar novamente
          </button>
        </div>
      </>
    );
  }

  const {
    empreendimentos,
    agregados,
    totalizadores,
    situacao_geral,
    dados_cadastro,
  } = dados;

  const valorTotalInadimplenciaAgregados =
    (empreendimentos || [])
      .flatMap(emp => emp.contratos || [])
      .reduce((acc, c) => acc + (c.valor_parcelas_vencidas || 0), 0) +
    (agregados || [])
      .flatMap(ag => ag.empreendimentos || [])
      .flatMap(emp => emp.contratos || [])
      .reduce((acc, c) => acc + (c.valor_parcelas_vencidas || 0), 0);

  return (
    <>
      <div className="situacao-container">
        <div className="situacao-header">
          <div className="situacao-header__title">
            <h1>Situação do Cliente</h1>
            <StatusBadge status={situacao_geral} size="lg" />
          </div>
          <p className="situacao-header__subtitle">
            Visão consolidada de todos os contratos e empreendimentos
          </p>
        </div>

        {dados_cadastro && (
          <div className="prospecto-card">
            <div className="prospecto-card__header">
              <div className="prospecto-card__avatar">
                <FiUser size={32} />
              </div>
              <div className="prospecto-card__title-area">
                <h2 className="prospecto-card__name">
                  <a
                    href={`${gerarAmbienteUrl(false)}/prospectos/lista/${
                      dados_cadastro.codigo_prospecto
                    }`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="prospecto-card__name-link"
                  >
                    {dados_cadastro.nome_cliente} - {format.cpf(cpf)}
                    <FiExternalLink size={16} />
                  </a>
                </h2>
                <span className="prospecto-card__profession">
                  {dados_cadastro.profissao || 'Profissão não informada'}
                </span>
              </div>
            </div>

            <div className="prospecto-card__content">
              <div className="prospecto-card__section">
                <h4 className="prospecto-card__section-title">
                  <FiMail size={16} />
                  Contato
                </h4>
                <div className="prospecto-card__grid">
                  <div className="prospecto-card__item">
                    <span className="prospecto-card__label">E-mail</span>
                    <span className="prospecto-card__value">
                      {dados_cadastro.email || '-'}
                    </span>
                  </div>
                  <div className="prospecto-card__item">
                    <span className="prospecto-card__label">Celular</span>
                    <span className="prospecto-card__value">
                      {format.celular(dados_cadastro.celular)}
                    </span>
                  </div>
                  <div className="prospecto-card__item">
                    <span className="prospecto-card__label">
                      Tel. Comercial
                    </span>
                    <span className="prospecto-card__value">
                      {format.phone(dados_cadastro.tel_comercial)}
                    </span>
                  </div>
                  <div className="prospecto-card__item">
                    <span className="prospecto-card__label">
                      Tel. Residencial
                    </span>
                    <span className="prospecto-card__value">
                      {format.phone(dados_cadastro.tel_residencial)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="prospecto-card__section">
                <h4 className="prospecto-card__section-title">
                  <FiMapPin size={16} />
                  Endereço
                </h4>
                <div className="prospecto-card__address">
                  <span className="prospecto-card__value">
                    {dados_cadastro.endereco || '-'}
                  </span>
                  <span className="prospecto-card__value">
                    {dados_cadastro.bairro && `${dados_cadastro.bairro} - `}
                    {dados_cadastro.cidade || ''}
                    {dados_cadastro.estado && `/${dados_cadastro.estado}`}
                  </span>
                  <span className="prospecto-card__value prospecto-card__value--muted">
                    CEP: {format.cep(dados_cadastro.cep)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="totalizadores-grid">
          <StatCard
            label="Contratos Financiados"
            value={totalizadores.total_contratos_financiados || 0}
            variant="primary"
            icon={FiFileText}
          />
          <StatCard
            label="Contratos Transferidos"
            value={totalizadores.total_contratos_transferidos || 0}
            variant="info"
            icon={FiUsers}
          />
          <StatCard
            label="Contratos Rescindidos"
            value={totalizadores.total_contratos_rescindidos || 0}
            variant="neutral"
            icon={FiAlertCircle}
          />
          <StatCard
            label="Contratos Quitados"
            value={totalizadores.total_contratos_quitados || 0}
            variant="success"
            icon={FiCheckCircle}
          />
          <StatCard
            label="Total Inadimplência"
            value={formatarMonetario(
              totalizadores.valor_total_inadimplencia > 0
                ? totalizadores.valor_total_inadimplencia
                : '0'
            )}
            variant={
              totalizadores.valor_total_inadimplencia > 0 ? 'danger' : 'success'
            }
            icon={FiDollarSign}
          />
          {valorTotalInadimplenciaAgregados > 0 && (
            <StatCard
              label="Total Geral"
              value={formatarMonetario(valorTotalInadimplenciaAgregados)}
              variant="danger"
              icon={FiUsers}
            />
          )}
        </div>

        {empreendimentos && empreendimentos.length > 0 && (
          <section className="situacao-section">
            <div className="situacao-section__header">
              <h2 className="situacao-section__title">
                <FiHome size={24} />
                Empreendimentos
              </h2>
              <span className="situacao-section__count">
                {empreendimentos.length} empreendimento(s)
              </span>
            </div>

            <div className="empreendimentos-list">
              {empreendimentos.map((emp, idx) => (
                <EmpreendimentoCard
                  key={idx}
                  empreendimento={emp}
                  defaultExpanded={idx === 0}
                />
              ))}
            </div>
          </section>
        )}

        {agregados && agregados.length > 0 && (
          <section className="situacao-section">
            <div className="situacao-section__header">
              <h2 className="situacao-section__title">
                <FiUsers size={24} />
                CPFs Vinculados
              </h2>
              <span className="situacao-section__count">
                {agregados.length} agregado(s)
              </span>
            </div>

            <div className="agregados-list">
              {agregados.map((agregado, idx) => (
                <AgregadoCard
                  key={idx}
                  agregado={agregado}
                  defaultExpanded={false}
                />
              ))}
            </div>
          </section>
        )}

        {(!empreendimentos || empreendimentos.length === 0) &&
          (!agregados || agregados.length === 0) && (
            <div className="situacao-empty">
              <FiFileText size={64} />
              <h3>Nenhum dado encontrado</h3>
              <p>
                Não foram encontrados contratos ou empreendimentos para este
                cliente.
              </p>
            </div>
          )}
      </div>
    </>
  );
};

export default CardSituacaoDetalhada;
